import React, { createContext, memo, PropsWithChildren, useContext, useMemo } from 'react';

import { type otaMachine } from '@sg/ota-installer';

import { type AuthMachineRefType, useInitAuthMachine } from '../AuthMachine/useInitAuthMachine';
import { useInitOtaActor } from '../OTA';

import type { ActorRefFrom as ActorRefFromV5 } from 'xstate5';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hosts the set of machines that must run before the rest of the app's provider tree mounts.
 * Other long-lived actors live in the global app state provider.
 */
export const EssentialMachinesProvider = memo((props: PropsWithChildren) => {
  const authMachineRef = useInitAuthMachine();
  const otaMachineRef = useInitOtaActor();

  const value = useMemo<EssentialMachinesContextValue>(
    () => ({ authMachineRef, otaMachineRef }),
    [authMachineRef, otaMachineRef],
  );

  return (
    <EssentialMachinesContext.Provider value={value}>
      {props.children}
    </EssentialMachinesContext.Provider>
  );
});

export const useEssentialMachines = () => {
  const context = useContext(EssentialMachinesContext);

  if (!context) {
    throw new Error('useEssentialMachines must be used within <EssentialMachinesProvider />');
  }

  return context;
};

// ─── Context ─────────────────────────────────────────────────────────────────

export const EssentialMachinesContext = createContext<EssentialMachinesContextValue | undefined>(
  undefined,
);

// ─── Types ───────────────────────────────────────────────────────────────────

type EssentialMachinesContextValue = {
  authMachineRef: AuthMachineRefType;
  otaMachineRef: ActorRefFromV5<typeof otaMachine>;
};
